// src/lib/caution-figee.js — LA PORTE UNIQUE « quelle caution pour CE séjour ? »
// (P-2.56, décisions de Dimitri du 30/10, n° 16 et 17). ⚠️ CHEMIN ARGENT.
//
// CommonJS pur, sans entrée-sortie : le serveur (require) et l'écran (import)
// lisent ce même fichier. Ne rien ajouter qui ne soit chargeable des deux côtés.
//
// Une seule question, une seule réponse, pour tous les lecteurs : la session
// Stripe (montant, empreinte ou prélèvement), le contrat, les messages, la
// page de paiement, la vitrine, la tâche de remboursement. Ainsi le voyageur
// ne lit jamais « empreinte » quand Stripe prélève.
//
// Ordre de lecture (le premier qui répond gagne) :
//   1. FIGÉE : la ligne porte `caution_figee_le` → son montant (0 = pas de
//      caution, VOULU : aucun repli sur le réglage du jour) et son seuil.
//      Une ligne figée illisible (seuil hors 1..4, montant hors 0..5 000) ne
//      vaut RIEN : on recalcule (origine « calcul ») et on le signale —
//      jamais un 0 deviné.
//   2. VERROUILLÉE (lignes d'avant le gel) : la ligne porte un montant > 0
//      posé à la création du lien de caution → ce montant, comme aujourd'hui ;
//      le seuil vient du réglage du logement.
//   3. CALCUL : le réglage du logement pour cette durée (paliers, sinon
//      caution_amount ; chambre puis logement parent) et son seuil.
//
// Sans paliers ni seuil réglés (cas réel au 30/10), le calcul rend EXACTEMENT
// le montant et le mode d'avant : resolveCautionFromLog et
// CAUTION_HOLD_MAX_NIGHTS.
//
// Au-delà de 4 nuits, le MONTANT n'est pas fixé par une décision (D-33) : ce
// module n'invente rien, il lit le palier réglé par Dimitri.

const {
  diagnostiquerCaution,
  cautionVoulueAZero,
  CAUTION_MONTANT_MAX_EUR,
} = require("./caution-amount.js");
const {
  seuilEmpreintePourLog,
  CAUTION_SEUIL_MAX_SUR,
} = require("./caution-mode.js");

const ORIGINES = Object.freeze({
  FIGEE: "figee",
  VERROUILLEE: "verrouillee",
  CALCUL: "calcul",
});

/**
 * Nombre de nuits entre deux dates « AAAA-MM-JJ » (ou ISO). 0 si illisible ou
 * si le départ n'est pas après l'arrivée (jamais deviné).
 */
function nuitsEntre(debut, fin) {
  const j = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ""));
    return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : NaN;
  };
  const n = Math.round((j(fin) - j(debut)) / 86400000);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function _seuilLisible(v) {
  const n = typeof v === "string" && /^\d+$/.test(v.trim()) ? Number(v) : v;
  return typeof n === "number" &&
    Number.isInteger(n) &&
    n >= 1 &&
    n <= CAUTION_SEUIL_MAX_SUR
    ? n
    : null;
}

function _montantLisible(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).trim());
  return Number.isFinite(n) && n >= 0 && n <= CAUTION_MONTANT_MAX_EUR
    ? n
    : null;
}

/**
 * Lit la partie figée d'une ligne (réservation, bloc ou demande).
 * @returns {{etat:"absente"}|{etat:"illisible"}|{etat:"figee", montant:number, seuil:number}}
 */
function lireFigee(ligne) {
  if (!ligne || typeof ligne !== "object" || !ligne.caution_figee_le) {
    return { etat: "absente" };
  }
  const montant = _montantLisible(ligne.caution_amount_eur);
  const seuil = _seuilLisible(ligne.caution_seuil_nuits);
  if (montant === null || seuil === null) return { etat: "illisible" };
  return { etat: "figee", montant, seuil };
}

function _mode(nuits, seuil) {
  // Même fail-safe que cautionModeForNights : 0 nuit, NaN → empreinte.
  return nuits > seuil ? "debit" : "empreinte";
}

/**
 * LA porte : caution d'un séjour.
 *
 * @param {object} args
 * @param {object|null} [args.figee] — la ligne (ou ses colonnes caution_*) ;
 *   lue pour « figée » puis « verrouillée ».
 * @param {object|null} [args.log] — logement aplati (flattenLogs, parentLog).
 * @param {number} [args.nuits] — durée du séjour (entier ≥ 1, sinon 0).
 * @param {{montant:number|null, zero?:boolean, seuil?:number}} [args.calcul]
 *   — résultat déjà calculé côté serveur (montant du réglage pour la durée,
 *   seuil du logement) quand le logement n'est pas fourni.
 * @returns {{ montant:number|null, zero:boolean, seuil:number,
 *             mode:"empreinte"|"debit", origine:"figee"|"verrouillee"|"calcul",
 *             anomalies:Array<object> }}
 *   montant : euros > 0, ou null (pas de caution, ou non définie) ;
 *   zero    : true si « pas de caution » est VOULU (0 figé ou réglé à 0).
 */
function cautionPourSejour({ figee = null, log = null, nuits = 0, calcul } = {}) {
  const n = Number.isInteger(nuits) && nuits > 0 ? nuits : 0;
  const anomalies = [];

  const f = lireFigee(figee);
  if (f.etat === "figee") {
    return {
      montant: f.montant > 0 ? f.montant : null,
      zero: f.montant === 0,
      seuil: f.seuil,
      mode: _mode(n, f.seuil),
      origine: ORIGINES.FIGEE,
      anomalies,
    };
  }
  if (f.etat === "illisible") anomalies.push({ motif: "figee_illisible" });

  const seuilCalcule =
    calcul && _seuilLisible(calcul.seuil) !== null
      ? calcul.seuil
      : seuilEmpreintePourLog(log);

  // Lignes d'avant le gel : montant posé à la création du lien (> 0 seulement,
  // comme `Number(row.caution_amount_eur) || …` le faisait).
  const verrou = Number(figee && figee.caution_amount_eur);
  if (f.etat === "absente" && Number.isFinite(verrou) && verrou > 0) {
    return {
      montant: verrou,
      zero: false,
      seuil: seuilCalcule,
      mode: _mode(n, seuilCalcule),
      origine: ORIGINES.VERROUILLEE,
      anomalies,
    };
  }

  let montant;
  let zero;
  if (calcul) {
    montant =
      Number.isFinite(Number(calcul.montant)) && Number(calcul.montant) > 0
        ? Number(calcul.montant)
        : null;
    zero = montant === null && calcul.zero === true;
  } else {
    // Les paliers ne sont lus qu'avec une durée lisible ; sans durée, la
    // résolution d'avant (caution_amount) — à l'identique.
    const opts = n > 0 ? { nuits: n } : undefined;
    const d = diagnostiquerCaution(log, opts);
    anomalies.push(...d.anomalies);
    montant = d.montant;
    zero = montant === null && cautionVoulueAZero(log, opts);
  }
  return {
    montant,
    zero,
    seuil: seuilCalcule,
    mode: _mode(n, seuilCalcule),
    origine: ORIGINES.CALCUL,
    anomalies,
  };
}

/**
 * Les valeurs à figer pour un séjour qui naît (montant 0 si « pas de caution »
 * voulu). null si la caution n'est PAS définie (rien à figer : on ne fige
 * jamais un 0 deviné).
 * @returns {{caution_amount_eur:number, caution_seuil_nuits:number}|null}
 */
function valeursAFiger(caution) {
  if (!caution) return null;
  if (caution.montant != null && caution.montant > 0) {
    return {
      caution_amount_eur: caution.montant,
      caution_seuil_nuits: caution.seuil,
    };
  }
  if (caution.zero === true) {
    return { caution_amount_eur: 0, caution_seuil_nuits: caution.seuil };
  }
  return null;
}

module.exports = {
  ORIGINES,
  nuitsEntre,
  lireFigee,
  cautionPourSejour,
  valeursAFiger,
};
